/**
 * The signed-in account, held once for the whole app.
 *
 * One provider rather than a hook per component, because three different parts of
 * the page need the same fact — the header, the pricing cards and the account
 * panel — and three copies of "am I signed in" is three things that disagree for a
 * moment after signing in or out. The session itself lives in localStorage because
 * that is what survives a reload; the entitlements live here because they are
 * cheap to re-read and must not be cached across a purchase.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ApiError,
  type Account,
  type Entitlement,
  fetchAccount,
  needsRenewal,
  readStoredSession,
  renewSession,
  signInWithPassword,
  signUpWithPassword,
  signInWithOAuth as oauthSignIn,
  writeStoredSession,
  type Session,
} from '../lib/api';

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in';

interface AuthValue {
  readonly status: AuthStatus;
  readonly session: Session | null;
  readonly account: Account | null;
  readonly entitlements: readonly Entitlement[];
  /** True while entitlements are being re-read after a change. */
  readonly refreshing: boolean;
  /**
   * The last failure, as thrown, or null. Held rather than rendered.
   *
   * `ApiError.message` is a getter that resolves its sentence against the language on
   * screen, so a failure raised before a language switch is *reported* in the new
   * language. Storing `caught.message` instead would freeze the sentence at the
   * moment it was thrown, and the sign-in form would show a Portuguese error under a
   * Spanish heading until the reader submitted again.
   *
   * Resolve it at render with `messageOf(thrown, fallback)`, which is also what
   * covers a throw that was never an `Error`.
   */
  readonly error: unknown;
  readonly needsConfirmation: boolean;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string): Promise<void>;
  signInWithOAuth(provider: 'google' | 'github'): Promise<void>;
  establishSession(session: Session): Promise<void>;
  signOut(): Promise<void>;
  refreshEntitlements(): Promise<void>;
  clearError(): void;
}

const AuthContext = createContext<AuthValue | null>(null);

/** The two catalogue entries, so an entitlement can be shown with the app's name. */
const APP_NAMES: Readonly<Record<string, string>> = {
  orobas_hipercube: 'OrobasHipercube',
  orobas_work: 'Orobas Work',
};

export function appNameFor(appId: string): string {
  return APP_NAMES[appId] ?? appId;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [entitlements, setEntitlements] = useState<readonly Entitlement[]>([]);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<unknown>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // A token that has been replaced or signed out must not be able to write its
  // entitlements back: the request that was in flight when the buyer clicked "sign
  // out" would otherwise repopulate the page they just left.
  const currentToken = useRef<string | null>(null);

  const loadAccount = useCallback(async (token: string) => {
    setRefreshing(true);
    try {
      const response = await fetchAccount(token);
      if (currentToken.current !== token) return;
      setAccount(response.account);
      setEntitlements(response.entitlements);
      setStatus('signed-in');
    } catch (caught) {
      if (currentToken.current !== token) return;
      // A 401 here means the stored token is no longer good — expired, or the
      // session was revoked elsewhere. Signing out locally is the honest response;
      // anything else would leave a page that looks signed in and is not.
      if (caught instanceof ApiError && caught.status === 401) {
        currentToken.current = null;
        writeStoredSession(null);
        setSession(null);
        setAccount(null);
        setEntitlements([]);
        setStatus('signed-out');
      } else {
        // A network or upstream fault is not a reason to sign anybody out. Held as
        // thrown, for the reason on `AuthValue.error`.
        setError(caught);
        setStatus('signed-in');
      }
    } finally {
      if (currentToken.current === token) setRefreshing(false);
    }
  }, []);

  /**
   * Brings a stored session back to life.
   *
   * A reload after an hour used to leave the reader signed out of the site while both
   * desktop apps were still working: nothing renewed the access token, so the API
   * answered 401 and the provider threw the session away. Renewing first is what makes
   * one account behave as one account on all three surfaces.
   */
  useEffect(() => {
    const stored = readStoredSession();
    if (stored === null) {
      setStatus('signed-out');
      return;
    }
    if (!needsRenewal(stored)) {
      currentToken.current = stored.accessToken;
      setSession(stored);
      void loadAccount(stored.accessToken);
      return;
    }
    void (async () => {
      try {
        const renewed = await renewSession(stored);
        // A session replaced while this was in flight belongs to somebody else — they
        // signed in, or out, in another tab.
        if (currentToken.current !== null && currentToken.current !== stored.accessToken) return;
        currentToken.current = renewed.accessToken;
        writeStoredSession(renewed);
        setSession(renewed);
        await loadAccount(renewed.accessToken);
      } catch {
        // A refresh token the server will not honour is a signed-out state, not a
        // message to show. Anything else is left alone: the stored token may still work.
        if (needsRenewal(stored)) {
          currentToken.current = null;
          writeStoredSession(null);
          setSession(null);
          setAccount(null);
          setEntitlements([]);
          setStatus('signed-out');
        } else {
          currentToken.current = stored.accessToken;
          setSession(stored);
          void loadAccount(stored.accessToken);
        }
      }
    })();
  }, [loadAccount]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      setNeedsConfirmation(false);
      try {
        const next = await signInWithPassword(email, password);
        currentToken.current = next.accessToken;
        writeStoredSession(next);
        setSession(next);
        await loadAccount(next.accessToken);
      } catch (caught) {
        setError(caught);
        throw caught;
      }
    },
    [loadAccount],
  );

  const signUp = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      // Reaching this line means GoTrue accepted the signup. It has autoconfirm off
      // on this deployment, so the account exists but is not usable until the
      // confirmation link is followed — which is why this state exists as its own
      // screen rather than as a message under the form.
      await signUpWithPassword(email, password);
      setNeedsConfirmation(true);
    } catch (caught) {
      setError(caught);
      throw caught;
    }
  }, []);

  /**
   * Starts a Google or GitHub sign-in.
   *
   * This never returns normally: it navigates the browser away to the provider.
   * The session is established when the provider sends the browser back to
   * /auth/oauth-callback, which calls `establishSession` with the result.
   */
  const signInWithOAuth = useCallback(async (provider: 'google' | 'github') => {
    setError(null);
    setNeedsConfirmation(false);
    try {
      await oauthSignIn(provider);
    } catch (caught) {
      setError(caught);
      throw caught;
    }
  }, []);

  /**
   * Takes over a session that was obtained out-of-band (Google or GitHub callback)
   * and makes it the active account. Mirrors `signIn` except it does not ask for
   * a password, because the provider already authenticated the person.
   */
  const establishSession = useCallback(
    async (next: Session) => {
      setError(null);
      setNeedsConfirmation(false);
      currentToken.current = next.accessToken;
      writeStoredSession(next);
      setSession(next);
      await loadAccount(next.accessToken);
    },
    [loadAccount],
  );

  const signOut = useCallback(async () => {
    // The local session is dropped — access token, refresh token and all — and GoTrue
    // is *not* called. Revoking the refresh token would be tidier, and would also sign
    // the reader out of m2on.org in another tab, which is not this page's decision to
    // make. The token is deleted from this machine instead and is then an ordinary
    // expired token.
    currentToken.current = null;
    writeStoredSession(null);
    setSession(null);
    setAccount(null);
    setEntitlements([]);
    setError(null);
    setNeedsConfirmation(false);
    setStatus('signed-out');
  }, []);

  const refreshEntitlements = useCallback(async () => {
    const token = currentToken.current;
    if (token === null) return;
    await loadAccount(token);
  }, [loadAccount]);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      session,
      account,
      entitlements,
      refreshing,
      error,
      needsConfirmation,
      signIn,
      signUp,
      signInWithOAuth,
      establishSession,
      signOut,
      refreshEntitlements,
      clearError,
    }),
    [status, session, account, entitlements, refreshing, error, needsConfirmation, signIn, signUp, signInWithOAuth, establishSession, signOut, refreshEntitlements, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * The signed-in account, or null when there is none.
 *
 * Throws rather than returning null when used outside the provider: a component
 * that forgot its provider is a bug in this codebase, and a silent null would turn
 * it into a blank section somebody notices on the deployed site instead.
 */
export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return value;
}

/** Whether this account may use one app right now. */
export function hasApp(entitlements: readonly Entitlement[], appId: string): boolean {
  return entitlements.some((entitlement) => entitlement.appId === appId);
}
